/**
 * Haulier Allocation Screen
 *
 * Mobile-first drag/tap interface for allocating whitelisted trucks to orders.
 * Designed for haulier dispatchers working on phones and tablets in the field.
 *
 * Layout:
 *  - Desktop: side-by-side panels (orders left, vehicles right)
 *  - Mobile: card-based flow — pick an order, then pick trucks
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck,
  Package,
  ArrowLeft,
  Search,
  X,
  Check,
  Loader,
  Plus,
  MapPin,
  User,
  ChevronRight,
  Shield,
  AlertCircle,
  Clock,
  ArrowRightLeft,
  Filter,
  RefreshCw,
  Minus,
  CheckCircle2,
} from 'lucide-react';
import orderService from '../../../../services/orderService';
import LexiBridgeLoader from '../../../common/LexiBridgeLoader';

const mono = { fontFamily: 'IBM Plex Mono, monospace' };
const body = { fontFamily: 'DM Sans, sans-serif' };

/* ── Priority colors ────────────────────────── */
const PRIORITY_STYLES = {
  Urgent: { bg: 'bg-red-500', text: 'text-white', dot: 'bg-red-400', ring: 'ring-red-200' },
  High: { bg: 'bg-amber-500', text: 'text-white', dot: 'bg-amber-400', ring: 'ring-amber-200' },
  Normal: { bg: 'bg-sky-500', text: 'text-white', dot: 'bg-sky-400', ring: 'ring-sky-200' },
  Low: { bg: 'bg-slate-400', text: 'text-white', dot: 'bg-slate-300', ring: 'ring-slate-200' },
};

const STATUS_DOTS = {
  Active: 'bg-emerald-400',
  'In Progress': 'bg-amber-400',
};

/* ── Order Card ─────────────────────────────── */
const OrderCard = ({ order, selected, onSelect, allocations }) => {
  const priority = PRIORITY_STYLES[order.order_priority] || PRIORITY_STYLES.Normal;
  const isActive = selected?.order_no === order.order_no;
  const orderAllocs = allocations.filter((a) => a.order_no === order.order_no);

  return (
    <button
      onClick={() => onSelect(order)}
      className={`
        w-full text-left rounded-2xl border-2 p-4 transition-all duration-200 active:scale-[0.98]
        ${
          isActive
            ? 'border-teal-500 bg-teal-50/40 shadow-md shadow-teal-100/50'
            : 'border-slate-200 bg-surface hover:border-slate-300 hover:shadow-sm'
        }
      `}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <span
            className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${priority.bg} ${priority.text}`}
          >
            {order.order_priority}
          </span>
          <div
            className={`w-2 h-2 rounded-full ${STATUS_DOTS[order.order_status] || 'bg-slate-300'}`}
          />
        </div>
        <div className="flex items-center gap-1.5">
          {orderAllocs.length > 0 && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full text-[10px] font-bold">
              <Truck size={10} />
              {orderAllocs.length}
            </span>
          )}
          <ChevronRight
            className={`w-4 h-4 transition-colors ${isActive ? 'text-teal-500' : 'text-slate-300'}`}
          />
        </div>
      </div>

      <p className="text-sm font-bold text-slate-900 mb-1" style={mono}>
        {order.order_no}
      </p>
      <p className="text-lg font-bold text-slate-800 mb-2" style={mono}>
        {parseFloat(order.order_size).toLocaleString()}{' '}
        <span className="text-xs font-medium text-slate-400">MT</span>
      </p>

      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <MapPin size={11} className="text-slate-400 flex-shrink-0" />
        <span className="truncate">
          {order.dispatch_location_name || 'N/A'}
          <span className="text-slate-300 mx-1">→</span>
          {order.receipt_location_name || 'N/A'}
        </span>
      </div>
    </button>
  );
};

/* ── Vehicle Card ───────────────────────────── */
const VehicleCard = ({ vehicle, onAllocate, allocating, isAllocated }) => {
  return (
    <div
      className={`
      rounded-2xl border-2 p-4 transition-all duration-200
      ${isAllocated ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-surface'}
    `}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center ${
              isAllocated ? 'bg-emerald-100' : 'bg-sky-50'
            }`}
          >
            <Truck size={18} className={isAllocated ? 'text-emerald-600' : 'text-sky-600'} />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900" style={mono}>
              {vehicle.plate_number}
            </p>
            <p className="text-[11px] text-slate-400 capitalize">
              {(vehicle.vehicle_type || 'other').replace('_', ' ')}
            </p>
          </div>
        </div>
        {isAllocated ? (
          <span className="flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-semibold">
            <CheckCircle2 size={13} />
            Allocated
          </span>
        ) : (
          <button
            onClick={() => onAllocate(vehicle)}
            disabled={allocating}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-bold transition-all active:scale-95 disabled:opacity-50 shadow-sm"
          >
            {allocating ? <Loader size={13} className="animate-spin" /> : <Plus size={13} />}
            Allocate
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {vehicle.hauler_name && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Shield size={11} className="text-slate-400" />
            <span className="truncate max-w-[120px]">{vehicle.hauler_name}</span>
          </div>
        )}
        {vehicle.driver_name && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <User size={11} className="text-slate-400" />
            <span className="truncate max-w-[120px]">{vehicle.driver_name}</span>
          </div>
        )}
        {vehicle.active_allocations > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <Clock size={11} />
            <span>{vehicle.active_allocations} active</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Allocation Row (current allocations for selected order) ── */
const AllocationRow = ({ alloc, onRemove, removing }) => (
  <div className="flex items-center justify-between px-4 py-3 bg-surface rounded-xl border border-slate-200 group">
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
        <Truck size={15} className="text-teal-600" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-slate-800" style={mono}>
          {alloc.plate_number}
        </p>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          {alloc.driver_name && <span>{alloc.driver_name}</span>}
          <span
            className={`capitalize px-1.5 py-0.5 rounded text-[10px] font-bold ${
              alloc.allocation_status === 'allocated'
                ? 'bg-sky-50 text-sky-600'
                : alloc.allocation_status === 'in_transit'
                  ? 'bg-amber-50 text-amber-600'
                  : alloc.allocation_status === 'completed'
                    ? 'bg-emerald-50 text-emerald-600'
                    : 'bg-slate-50 text-slate-500'
            }`}
          >
            {alloc.allocation_status}
          </span>
        </div>
      </div>
    </div>
    <button
      onClick={() => onRemove(alloc.allocation_id)}
      disabled={removing}
      className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100 sm:opacity-100"
      title="Remove allocation"
    >
      {removing ? <Loader size={14} className="animate-spin" /> : <Minus size={14} />}
    </button>
  </div>
);

/* ═══════════════════════════════════════════════ */
const HaulierAllocation = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderSearch, setOrderSearch] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [allocatingVehicle, setAllocatingVehicle] = useState(null);
  const [removingAlloc, setRemovingAlloc] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Mobile view state
  const [mobileView, setMobileView] = useState('orders'); // 'orders' | 'vehicles'

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [ordersRes, vehiclesRes, allocsRes] = await Promise.all([
        orderService.getAvailableOrders(),
        orderService.getAvailableVehicles(),
        orderService.getOrderAllocations(),
      ]);
      setOrders(ordersRes.orders || []);
      setVehicles(vehiclesRes.vehicles || []);
      setAllocations(allocsRes.allocations || []);
    } catch (err) {
      console.error('Failed to load allocation data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    setMobileView('vehicles');
  };

  const handleAllocate = async (vehicle) => {
    if (!selectedOrder) return;
    setAllocatingVehicle(vehicle.vehicle_id);
    try {
      await orderService.allocateVehicleToOrder(
        selectedOrder.order_no,
        vehicle.vehicle_id,
        vehicle.driver_id || null
      );
      // Reload allocations
      const allocsRes = await orderService.getOrderAllocations();
      setAllocations(allocsRes.allocations || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to allocate vehicle');
    } finally {
      setAllocatingVehicle(null);
    }
  };

  const handleRemoveAllocation = async (allocationId) => {
    setRemovingAlloc(allocationId);
    try {
      await orderService.removeAllocation(allocationId);
      setAllocations((prev) => prev.filter((a) => a.allocation_id !== allocationId));
    } catch (err) {
      alert('Failed to remove allocation');
    } finally {
      setRemovingAlloc(null);
    }
  };

  // Filter orders
  const filteredOrders = orders.filter((o) => {
    if (!orderSearch) return true;
    const q = orderSearch.toLowerCase();
    return (
      (o.order_no || '').toLowerCase().includes(q) ||
      (o.dispatch_location_name || '').toLowerCase().includes(q) ||
      (o.receipt_location_name || '').toLowerCase().includes(q)
    );
  });

  // Filter vehicles
  const filteredVehicles = vehicles.filter((v) => {
    if (!vehicleSearch) return true;
    const q = vehicleSearch.toLowerCase();
    return (
      (v.plate_number || '').toLowerCase().includes(q) ||
      (v.hauler_name || '').toLowerCase().includes(q) ||
      (v.driver_name || '').toLowerCase().includes(q)
    );
  });

  // Allocations for selected order
  const currentAllocations = selectedOrder
    ? allocations.filter((a) => a.order_no === selectedOrder.order_no)
    : [];

  // Vehicles already allocated to selected order
  const allocatedVehicleIds = new Set(currentAllocations.map((a) => a.vehicle_id));

  if (loading) {
    return (
      <div
        style={{ ...body, backgroundColor: 'var(--app-bg)' }}
        className="flex items-center justify-center min-h-[60vh]"
      >
        <LexiBridgeLoader message="Loading allocation data" />
      </div>
    );
  }

  return (
    <div style={body} className="min-h-screen bg-slate-50">
      {/* ── Top Bar ───────────────────────── */}
      <div className="sticky top-0 z-30 bg-surface/90 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (mobileView === 'vehicles' && window.innerWidth < 1024) {
                    setMobileView('orders');
                    return;
                  }
                  navigate('/order-management');
                }}
                className="p-2 -ml-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors lg:hover:bg-transparent"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-base sm:text-lg font-bold text-slate-900">Truck Allocation</h1>
                <p className="text-xs text-slate-400 hidden sm:block">
                  Assign whitelisted vehicles to active orders
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2.5 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              </button>

              {/* Mobile tab toggle */}
              <div className="flex lg:hidden bg-slate-100 rounded-xl p-1">
                <button
                  onClick={() => setMobileView('orders')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    mobileView === 'orders'
                      ? 'bg-surface text-slate-900 shadow-sm'
                      : 'text-slate-500'
                  }`}
                >
                  Orders
                </button>
                <button
                  onClick={() => setMobileView('vehicles')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    mobileView === 'vehicles'
                      ? 'bg-surface text-slate-900 shadow-sm'
                      : 'text-slate-500'
                  }`}
                >
                  Trucks
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Layout ──────────────────── */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex flex-col lg:flex-row gap-5">
          {/* ── Orders Panel ── */}
          <div
            className={`lg:w-[420px] xl:w-[460px] flex-shrink-0 ${
              mobileView === 'vehicles' ? 'hidden lg:block' : ''
            }`}
          >
            <div className="lg:sticky lg:top-[65px]">
              {/* Search */}
              <div className="relative mb-4">
                <Search
                  size={15}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="Search orders..."
                  className="w-full pl-10 pr-9 py-3 bg-surface border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                />
                {orderSearch && (
                  <button
                    onClick={() => setOrderSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Order count */}
              <div className="flex items-center justify-between mb-3 px-1">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Active Orders
                </p>
                <span
                  className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-bold"
                  style={mono}
                >
                  {filteredOrders.length}
                </span>
              </div>

              {/* Order list */}
              <div className="space-y-2.5 max-h-[calc(100vh-200px)] overflow-y-auto pr-1 pb-4 custom-scroll">
                {filteredOrders.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500 font-medium">No orders found</p>
                    <p className="text-xs text-slate-400 mt-1">Try adjusting your search</p>
                  </div>
                ) : (
                  filteredOrders.map((order) => (
                    <OrderCard
                      key={order.order_no}
                      order={order}
                      selected={selectedOrder}
                      onSelect={handleSelectOrder}
                      allocations={allocations}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* ── Vehicles Panel ── */}
          <div className={`flex-1 min-w-0 ${mobileView === 'orders' ? 'hidden lg:block' : ''}`}>
            {!selectedOrder ? (
              /* No order selected state */
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 bg-slate-100 rounded-3xl flex items-center justify-center mb-5">
                  <ArrowRightLeft className="w-9 h-9 text-slate-300" />
                </div>
                <p className="text-base font-semibold text-slate-600 mb-1">
                  Select an order to allocate trucks
                </p>
                <p className="text-sm text-slate-400 max-w-xs">
                  Choose an active order from the list, then assign available whitelisted vehicles
                </p>
              </div>
            ) : (
              <div>
                {/* Selected order summary */}
                <div className="bg-surface rounded-2xl border border-slate-200 p-4 sm:p-5 mb-5 shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2.5 mb-1.5">
                        <span
                          className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                            (
                              PRIORITY_STYLES[selectedOrder.order_priority] ||
                              PRIORITY_STYLES.Normal
                            ).bg
                          } ${(PRIORITY_STYLES[selectedOrder.order_priority] || PRIORITY_STYLES.Normal).text}`}
                        >
                          {selectedOrder.order_priority}
                        </span>
                        <p className="text-base font-bold text-slate-900" style={mono}>
                          {selectedOrder.order_no}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <MapPin size={11} className="text-emerald-500" />
                          {selectedOrder.dispatch_location_name || 'N/A'}
                        </span>
                        <span className="text-slate-300">→</span>
                        <span className="flex items-center gap-1">
                          <MapPin size={11} className="text-red-400" />
                          {selectedOrder.receipt_location_name || 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xs text-slate-400">Order Size</p>
                        <p className="text-lg font-bold text-slate-800" style={mono}>
                          {parseFloat(selectedOrder.order_size).toLocaleString()}
                          <span className="text-xs font-medium text-slate-400 ml-1">MT</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">Trucks</p>
                        <p className="text-lg font-bold text-teal-600" style={mono}>
                          {currentAllocations.length}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Current allocations */}
                {currentAllocations.length > 0 && (
                  <div className="mb-5">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2.5 px-1">
                      Allocated Trucks
                    </p>
                    <div className="space-y-2">
                      {currentAllocations.map((alloc) => (
                        <AllocationRow
                          key={alloc.allocation_id}
                          alloc={alloc}
                          onRemove={handleRemoveAllocation}
                          removing={removingAlloc === alloc.allocation_id}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Vehicle search */}
                <div className="relative mb-4">
                  <Search
                    size={15}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="text"
                    value={vehicleSearch}
                    onChange={(e) => setVehicleSearch(e.target.value)}
                    placeholder="Search plate, haulier, or driver..."
                    className="w-full pl-10 pr-9 py-3 bg-surface border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                  />
                  {vehicleSearch && (
                    <button
                      onClick={() => setVehicleSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {/* Vehicle count */}
                <div className="flex items-center justify-between mb-3 px-1">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Available Vehicles
                  </p>
                  <div className="flex items-center gap-2">
                    <Shield size={12} className="text-teal-500" />
                    <span
                      className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-bold"
                      style={mono}
                    >
                      {filteredVehicles.length} whitelisted
                    </span>
                  </div>
                </div>

                {/* Vehicle grid */}
                {filteredVehicles.length === 0 ? (
                  <div className="text-center py-12 bg-surface rounded-2xl border border-slate-200">
                    <Truck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500 font-medium">No vehicles found</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {vehicleSearch
                        ? 'Try a different search'
                        : 'Whitelist vehicles to make them available'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 pb-6">
                    {/* Show allocated first, then unallocated */}
                    {filteredVehicles
                      .sort((a, b) => {
                        const aAlloc = allocatedVehicleIds.has(a.vehicle_id);
                        const bAlloc = allocatedVehicleIds.has(b.vehicle_id);
                        if (aAlloc && !bAlloc) return 1;
                        if (!aAlloc && bAlloc) return -1;
                        return 0;
                      })
                      .map((vehicle) => (
                        <VehicleCard
                          key={vehicle.vehicle_id}
                          vehicle={vehicle}
                          onAllocate={handleAllocate}
                          allocating={allocatingVehicle === vehicle.vehicle_id}
                          isAllocated={allocatedVehicleIds.has(vehicle.vehicle_id)}
                        />
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Mobile FAB: Quick back to orders ── */}
      {mobileView === 'vehicles' && selectedOrder && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 lg:hidden z-20">
          <button
            onClick={() => setMobileView('orders')}
            className="flex items-center gap-2 px-5 py-3 bg-slate-900 dark:bg-slate-50 text-white rounded-2xl shadow-xl shadow-slate-900/30 dark:shadow-black/30 text-sm font-semibold active:scale-95 transition-transform"
          >
            <Package size={16} />
            {currentAllocations.length} truck{currentAllocations.length !== 1 ? 's' : ''} allocated
          </button>
        </div>
      )}
    </div>
  );
};

export default HaulierAllocation;
